import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import {
  ShieldAlert,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Heart,
  MessageSquare,
  Filter,
  RefreshCw,
  Eye,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import type { AnonymousReport } from '@/types/legacy';
import {
  categoryLabels,
  severityLabels,
  statusLabels,
  contactTypeLabels,
  anonymousReportStatuses,
} from '@/schemas/anonymousReportSchemas';

const statusIcons: Record<string, React.ReactNode> = {
  pending: <Clock className="w-4 h-4" />,
  reviewing: <AlertCircle className="w-4 h-4" />,
  in_progress: <AlertCircle className="w-4 h-4" />,
  resolved: <CheckCircle2 className="w-4 h-4" />,
  closed: <XCircle className="w-4 h-4" />,
};

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30',
  reviewing: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
  in_progress: 'bg-purple-500/10 text-purple-600 border-purple-500/30',
  resolved: 'bg-green-500/10 text-green-600 border-green-500/30',
  closed: 'bg-gray-500/10 text-gray-600 border-gray-500/30',
};

const severityColors: Record<string, string> = {
  low: 'bg-green-500/10 text-green-600',
  medium: 'bg-yellow-500/10 text-yellow-600',
  high: 'bg-orange-500/10 text-orange-600',
  critical: 'bg-red-500/10 text-red-600',
};

export function AnonymousReportsAdmin() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [reports, setReports] = useState<AnonymousReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [selectedReport, setSelectedReport] = useState<AnonymousReport | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state for editing
  const [editStatus, setEditStatus] = useState('');
  const [editAdminNotes, setEditAdminNotes] = useState('');
  const [editAdminResponse, setEditAdminResponse] = useState('');

  const fetchReports = async () => {
    setLoading(true);
    try {
      let query = api
        .from('anonymous_reports')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (filterStatus !== 'all') {
        query = query.eq('status', filterStatus);
      }

      const { data, error } = await query;
      if (error) throw error;
      setReports(data || []);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los reportes',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [filterStatus]);

  const handleOpenReport = (report: AnonymousReport) => {
    setSelectedReport(report);
    setEditStatus(report.status);
    setEditAdminNotes(report.admin_notes || '');
    setEditAdminResponse(report.admin_response || '');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!selectedReport) return;

    setSaving(true);
    try {
      const payload: any = {
        status: editStatus,
        admin_notes: editAdminNotes || null,
        admin_response: editAdminResponse || null,
      };

      if (editStatus === 'resolved' && selectedReport.status !== 'resolved') {
        payload.resolved_at = new Date().toISOString();
      }

      const { error } = await api
        .from('anonymous_reports')
        .update(payload)
        .eq('id', selectedReport.id);

      if (error) throw error;

      toast({
        title: 'Guardado',
        description: 'El reporte ha sido actualizado',
      });

      setDialogOpen(false);
      fetchReports();
      queryClient.invalidateQueries({ queryKey: ['anonymous_reports'] });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo actualizar el reporte',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const pendingCount = reports.filter(r => r.status === 'pending').length;
  const healthRequestCount = reports.filter(r => r.request_professional_contact && r.status !== 'resolved' && r.status !== 'closed').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-primary" />
            Reportes Anónimos
          </h3>
          <p className="text-sm text-muted-foreground">
            Gestiona los reportes anónimos de los empleados
          </p>
        </div>
        <Button variant="outline" onClick={fetchReports}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <Card className="p-3 text-center">
          <p className="text-2xl font-bold">{reports.length}</p>
          <p className="text-xs text-muted-foreground">Total</p>
        </Card>
        <Card className="p-3 text-center">
          <p className="text-2xl font-bold text-yellow-500">{pendingCount}</p>
          <p className="text-xs text-muted-foreground">Pendientes</p>
        </Card>
        <Card className="p-3 text-center">
          <p className="text-2xl font-bold text-pink-500">{healthRequestCount}</p>
          <p className="text-xs text-muted-foreground">Solicitan profesional</p>
        </Card>
        <Card className="p-3 text-center">
          <p className="text-2xl font-bold text-green-500">
            {reports.filter(r => r.status === 'resolved').length}
          </p>
          <p className="text-xs text-muted-foreground">Resueltos</p>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <Filter className="w-4 h-4 text-muted-foreground" />
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {anonymousReportStatuses.map((status) => (
              <SelectItem key={status} value={status}>
                {statusLabels[status]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
        </div>
      ) : reports.length === 0 ? (
        <Card className="p-12 text-center">
          <ShieldAlert className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <p className="text-lg text-muted-foreground">No hay reportes anónimos</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {reports.map((report) => (
            <Card
              key={report.id}
              className="p-4 hover:shadow-md transition-all cursor-pointer"
              onClick={() => handleOpenReport(report)}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <code className="text-sm font-mono text-primary">{report.tracking_code}</code>
                    <Badge className={`${statusColors[report.status]} border text-xs`}>
                      {statusIcons[report.status]}
                      <span className="ml-1">{statusLabels[report.status as keyof typeof statusLabels]}</span>
                    </Badge>
                    <Badge className={`${severityColors[report.severity]} text-xs`}>
                      {severityLabels[report.severity as keyof typeof severityLabels]}
                    </Badge>
                    {report.request_professional_contact && (
                      <Badge variant="outline" className="border-pink-500 text-pink-500 text-xs">
                        <Heart className="w-3 h-3 mr-1" />
                        Solicita profesional
                      </Badge>
                    )}
                  </div>
                  <p className="font-medium">{report.subject}</p>
                  <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                    {report.description}
                  </p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                    <span>{categoryLabels[report.category as keyof typeof categoryLabels]}</span>
                    <span>
                      {formatDistanceToNow(new Date(report.created_at), { addSuffix: true, locale: es })}
                    </span>
                  </div>
                </div>
                <Button variant="ghost" size="icon">
                  <Eye className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5" />
              Reporte {selectedReport?.tracking_code}
            </DialogTitle>
          </DialogHeader>

          {selectedReport && (
            <div className="space-y-4">
              {/* Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Categoría</Label>
                  <p className="font-medium">
                    {categoryLabels[selectedReport.category as keyof typeof categoryLabels]}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Urgencia</Label>
                  <p className="font-medium">
                    {severityLabels[selectedReport.severity as keyof typeof severityLabels]}
                  </p>
                </div>
              </div>

              <div>
                <Label className="text-muted-foreground">Asunto</Label>
                <p className="font-medium">{selectedReport.subject}</p>
              </div>

              <div>
                <Label className="text-muted-foreground">Descripción</Label>
                <Card className="p-3 mt-1 bg-muted/50">
                  <p className="text-sm whitespace-pre-wrap">{selectedReport.description}</p>
                </Card>
              </div>

              {selectedReport.request_professional_contact && (
                <Card className="p-4 border-pink-500/30 bg-pink-500/5">
                  <div className="flex items-center gap-2 text-pink-600">
                    <Heart className="w-5 h-5" />
                    <span className="font-medium">
                      Solicita contacto con profesional de{' '}
                      {contactTypeLabels[selectedReport.contact_type as keyof typeof contactTypeLabels]}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    Responde con instrucciones para que el empleado pueda contactar con RRHH de forma voluntaria.
                  </p>
                </Card>
              )}

              <hr />

              {/* Admin form */}
              <div className="space-y-4">
                <div>
                  <Label>Estado</Label>
                  <Select value={editStatus} onValueChange={setEditStatus}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {anonymousReportStatuses.map((status) => (
                        <SelectItem key={status} value={status}>
                          {statusLabels[status]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Notas internas (no visibles para el empleado)</Label>
                  <Textarea
                    className="mt-1"
                    placeholder="Notas para uso interno..."
                    rows={2}
                    value={editAdminNotes}
                    onChange={(e) => setEditAdminNotes(e.target.value)}
                  />
                </div>

                <div>
                  <Label className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    Respuesta pública (visible al consultar el código)
                  </Label>
                  <Textarea
                    className="mt-1"
                    placeholder="Escribe una respuesta que el empleado verá al consultar su código..."
                    rows={3}
                    value={editAdminResponse}
                    onChange={(e) => setEditAdminResponse(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Esta respuesta será visible para cualquiera que consulte el código de seguimiento.
                  </p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
